/** Every user-facing interface string. Content copy comes from the API. */
export const en = {
  brand: {
    wordmark: 'Lynn',
    standard: 'Character Standard',
    drawingNumber: 'LYN-STD',
  },
  footer: {
    eyebrow: 'Open character standard',
    title: 'See how Lynn is made.',
    github: 'GitHub',
    friends: 'Friends',
    sticker: 'sticker.stv.pm',
    home: 'stv.pm',
    social: 'Social',
    twitter: 'stv_lynn on X',
  },
  nav: {
    label: 'Sections',
    home: 'Drawing sheet',
    character: 'Character',
    wardrobe: 'Wardrobe',
    gallery: 'Art book',
    ui: 'UI',
    sticker: 'Stickers',
    pv: 'PV',
    specs: 'Specifications',
    open: 'Open navigation',
    close: 'Close navigation',
    skip: 'Skip to content',
  },
  device: {
    haptics: 'Touch feedback',
    denied: 'Motion access was denied. Allow it in browser settings to use tilt.',
    unavailable: 'Tilt is unavailable. Use a phone with motion sensors over HTTPS.',
    noHaptics: 'This browser does not support vibration.',
  },
  theme: {
    label: 'Theme',
    light: 'Whiteprint',
    dark: 'Blueprint',
    system: 'System',
  },
  common: {
    loading: 'Loading',
    retry: 'Try again',
    copy: 'Copy',
    copied: 'Copied',
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    source: 'Source',
    play: 'Play',
    replay: 'Replay',
    light: 'Light',
    dark: 'Dark',
  },
  verdict: {
    do: 'Do',
    dont: 'Don’t',
    note: 'Note',
  },
  errors: {
    title: 'This sheet did not load',
    notFoundTitle: 'No such sheet',
    notFoundBody: 'The drawing you asked for is not in this set.',
    backHome: 'Back to the drawing sheet',
  },
  home: {
    cta: 'Read the standard',
    ctaSecondary: 'Open the wardrobe',
    indexTitle: 'The set',
    pillarsTitle: 'Principles',
    sheets: {
      character: 'Identity, proportion and the traits that never change.',
      wardrobe: 'Six sanctioned outfits, one canonical.',
      gallery: 'Finished illustrations and their palettes.',
      ui: 'Tokens, type, motion and components.',
      sticker: 'Canvas, captions and the prompt template.',
      pv: 'Format, costume lock and production rules.',
    },
  },
  blueprint: {
    title: 'Front elevation',
    scale: 'Scale',
    scaleValue: '1 : 8',
    sheet: 'Sheet',
    sheetValue: '01 / 06',
    revision: 'Rev',
    revisionValue: 'C',
    heads: 'heads',
    headUnit: 'HU',
    centerline: 'CL',
    shoulder: 'Shoulder',
    hem: 'Hem',
    figureLabel: 'Line drawing of Lynn in the signature outfit, annotated like an engineering drawing.',
    viewLabel: 'Character view',
    frontView: 'Front',
    leftView: 'Left',
    backView: 'Back',
    rightView: 'Right',
    previousView: 'Previous view',
    nextView: 'Next view',
    viewFigureLabel: '{view} view of Lynn in the signature outfit, with identity and proportion annotations.',
  },
  character: {
    title: 'Character',
    facts: 'Specification',
    credits: 'Credits',
    pillars: 'Principles',
    traits: 'Identity traits',
    traitsHint: 'Click a trait to inspect it in color.',
    fullFigure: 'View full figure',
    palette: 'Character palette',
    proportion: 'Proportion',
    doNot: 'Never',
    prompt: 'Drawing prompt',
    promptDescription: 'Identity, proportions, colors and drawing constraints, ready for your image model.',
    copyPrompt: 'Copy drawing prompt',
    previewPrompt: 'Preview prompt',
    promptCopied: 'Prompt copied',
    promptCopyError: 'Clipboard access failed. Select and copy the prompt below.',
    promptTemplate:
      'Draw {name} in her canonical signature outfit.\n{tagline}\n\nIdentity traits:\n{traits}\n\nProportions:\n{proportion}\n\nColor palette:\n{palette}\n\nCreative principles:\n{principles}\n\nDo not:\n{restrictions}',
  },
  wardrobe: {
    title: 'Wardrobe',
    lead: 'The face, hair, glasses and beret stay fixed. Only the garments change.',
    list: 'Outfits',
    garments: 'Garments',
    palette: 'Palette',
    source: 'Source',
    canonical: 'Canonical',
  },
  gallery: {
    title: 'Art book',
    lead: 'Quiet figures in loud, impossible places.',
    motifs: 'Motifs',
    palette: 'Palette',
    open: 'Open plate',
  },
  ui: {
    title: 'UI specification',
    lead: 'Navy ink, silver-white paper and one cornflower accent. Every value below is live: it is the CSS this site runs on.',
    principles: 'Principles',
    ramps: 'Color ramps',
    semantic: 'Semantic colors',
    contrast: 'Contrast',
    contrastRatio: 'Ratio',
    contrastGrade: 'Grade',
    contrastPair: 'Pair',
    theme: 'Theme',
    typography: 'Typography',
    families: 'Families',
    scale: 'Type scale',
    space: 'Space',
    radius: 'Radius',
    elevation: 'Elevation',
    motion: 'Motion',
    easing: 'Easing',
    duration: 'Duration',
    components: 'Components',
    glyph: 'Aa 琳',
    typeSample: 'Lynn keeps still while the city moves',
    playHint: 'Click to replay',
    token: 'Token',
    value: 'Value',
    specimen: {
      primary: 'Continue',
      accent: 'Book a session',
      ghost: 'Cancel',
      badge: 'Draft',
      badgeAccent: 'Canonical',
      inputLabel: 'Caption',
      inputPlaceholder: 'Twelve characters at most',
      switchLabel: 'Reduce motion preview',
      button: 'Button',
      tagGroup: 'Tag · Segmented',
      fieldGroup: 'Field · Switch',
    },
  },
  sticker: {
    title: 'Sticker specification',
    canvas: 'Canvas',
    safeArea: 'Safe area',
    outline: 'Outline',
    field: 'Field',
    rules: 'Rules',
    composer: 'Prompt composer',
    composerText: 'Caption',
    composerAction: 'Action',
    composerSubmit: 'Compose prompt',
    composerTemplate: 'Template',
    composerResult: 'Prompt',
    composerCount: '{count} / {max}',
    set: 'The set',
    filterAll: 'All',
    filterCaption: 'Captioned',
    filterExpression: 'Expression only',
    search: 'Search stickers',
    empty: 'No sticker matches that search.',
    license: 'License',
    channels: 'Channels',
    previewSizes: 'Chat size and source size',
  },
  pv: {
    title: 'PV specification',
    format: 'Delivery format',
    resolution: 'Resolution',
    frameRate: 'Frame rate',
    runtime: 'Runtime',
    frames: 'Frames',
    audio: 'Audio',
    fpsUnit: 'fps',
    identity: 'Identity lock',
    costumes: 'Costume lock',
    rules: 'Rules',
    pipeline: 'Pipeline',
    stills: 'Stills',
    timeline: 'Timeline',
    excerpt: 'Excerpt',
  },
} as const;
